import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { serviceOf, todayIso } from '../data/common';

const KEY = 'tripfresh.bookings.v1';
const BookingsCtx = createContext(null);

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    // Sample bookings saved by older versions are dropped.
    if (raw) return JSON.parse(raw).filter((b) => !b.sample);
  } catch { /* storage unavailable */ }
  return [];
}

/**
 * A booking belongs to the account that made it or, for a guest checkout, to the
 * account with the email it was booked under. Signed-out visitors have no trips.
 */
const ownedBy = (user) => (b) => {
  if (!user) return false;
  if (b.userId) return b.userId === user.id;
  return Boolean(user.email) && b.contact?.email?.trim().toLowerCase() === user.email.toLowerCase();
};

const rand = (n) => Array.from({ length: n }, () => 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)]).join('');

export function BookingsProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [all, setAll] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* ignore */ }
  }, [all]);

  const addBooking = useCallback((data) => {
    const booking = {
      ...data,
      userId,
      id: `TF${serviceOf(data.module).prefix}${rand(7)}`,
      ref: data.ref || rand(6),
      status: data.status || 'confirmed',
      createdAt: new Date().toISOString(),
    };
    setAll((list) => [booking, ...list]);
    return booking;
  }, [userId]);

  const cancelBooking = useCallback((id, refund) => {
    setAll((list) => list.map((b) => (b.id === id
      ? { ...b, status: 'cancelled', cancelledAt: new Date().toISOString(), refund }
      : b)));
  }, []);

  const updateBooking = useCallback((id, patch) => {
    setAll((list) => list.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, []);

  const bookings = useMemo(() => all.filter(ownedBy(user)), [all, user]);

  const value = useMemo(() => ({
    bookings,
    addBooking,
    cancelBooking,
    updateBooking,
    // Guest bookings stay reachable by ID, so a guest can open their confirmation and documents.
    getBooking: (id) => all.find((b) => b.id === id && (!b.userId || b.userId === userId)),
  }), [all, bookings, userId, addBooking, cancelBooking, updateBooking]);

  return <BookingsCtx.Provider value={value}>{children}</BookingsCtx.Provider>;
}

export const useBookings = () => useContext(BookingsCtx);

export function bookingStatus(b) {
  if (b.status === 'cancelled') return 'cancelled';
  if (b.status === 'held') return 'held';
  return (b.endDate || b.travelDate) < todayIso() ? 'completed' : 'upcoming';
}

export const statusLabel = { upcoming: 'Confirmed', held: 'On hold', completed: 'Completed', cancelled: 'Cancelled' };
